import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, CheckCircle2, ClipboardList, BarChart3, Target } from 'lucide-react';
import { FloatingShapes } from './FloatingShapes';
interface LandingProps {
  onStart: () => void;
}
export function Landing({
  onStart
}: LandingProps) {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100
      }
    }
  };
  return <div className="min-h-screen bg-peach relative overflow-hidden flex flex-col items-center justify-center py-20 px-6" data-id="element-61">
      <FloatingShapes data-id="element-62" />

      <motion.div className="max-w-3xl w-full relative z-10 text-center" variants={containerVariants} initial="hidden" animate="visible" data-id="element-63">
        <motion.div variants={itemVariants} className="inline-flex items-center gap-2 bg-white/50 backdrop-blur-sm px-4 py-2 rounded-full text-grape font-bold text-sm mb-8 shadow-sm" data-id="element-64">
          <Sparkles size={16} data-id="element-65" />
          <span data-id="element-66">Free Assessment for Marketing Leaders</span>
        </motion.div>

        <motion.h1 variants={itemVariants} className="text-5xl md:text-7xl font-extrabold text-ink leading-tight mb-6 tracking-tight" data-id="element-67">
          Hit your{' '}
          <span className="text-grape relative whitespace-nowrap" data-id="element-68">
            revenue goals
            <svg className="absolute -bottom-2 left-0 w-full h-3 text-sunshine" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-69">
              <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="4" fill="transparent" data-id="element-70" />
            </svg>
          </span>{' '}
          faster with Automation
        </motion.h1>

        <motion.p variants={itemVariants} className="text-xl md:text-2xl text-ink/70 font-medium mb-12 max-w-2xl mx-auto" data-id="element-71">
          See which marketing tasks AI should automate first.
        </motion.p>

        <motion.div variants={itemVariants} className="flex flex-col items-center gap-4" data-id="element-72">
          <motion.button whileHover={{
          scale: 1.05,
          y: -4
        }} whileTap={{
          scale: 0.95
        }} onClick={onStart} className="bg-ink text-white text-xl font-bold px-10 py-5 rounded-full shadow-soft-lg hover:shadow-soft flex items-center gap-3 transition-all" data-id="element-73">
            Start prioritizing
            <ArrowRight size={24} data-id="element-74" />
          </motion.button>
          <p className="text-sm font-semibold text-ink/50 flex items-center gap-2" data-id="element-75">
            <CheckCircle2 size={14} data-id="element-76" /> Free • Takes 2 minutes • No signup until
            results
          </p>
        </motion.div>

        <motion.div variants={itemVariants} className="mt-28" data-id="element-77">
          <p className="text-xs font-extrabold text-ink/50 uppercase tracking-[0.2em] mb-8 text-center" data-id="element-78">
            How it works
          </p>

          {/* Desktop: horizontal timeline */}
          <div className="hidden md:block relative" data-id="element-79">
            {/* connector line */}
            <div className="absolute top-8 left-[16.66%] right-[16.66%] h-1 bg-white/80 rounded-full" data-id="element-80" />
            <div className="absolute top-8 left-[16.66%] w-[66.66%] h-1 rounded-full bg-gradient-to-r from-coral via-sunshine to-leaf opacity-60" data-id="element-81" />

            <div className="relative grid grid-cols-3 gap-8" data-id="element-82">
              {[{
              num: 1,
              Icon: ClipboardList,
              bg: 'bg-coral',
              textColor: 'text-white',
              title: 'Score your tasks',
              desc: 'Rate your weekly marketing tasks by time spent, impact, and repeatability.'
            }, {
              num: 2,
              Icon: BarChart3,
              bg: 'bg-sunshine',
              textColor: 'text-ink',
              title: 'See the leaderboard',
              desc: 'Our algorithm calculates the perfect priority score for every single task.'
            }, {
              num: 3,
              Icon: Target,
              bg: 'bg-leaf',
              textColor: 'text-white',
              title: 'Get your Top 3',
              desc: 'Walk away with a clear, actionable plan on what to hand off to AI first.'
            }].map((step, i) => <motion.div key={step.num} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + i * 0.15,
              type: 'spring',
              stiffness: 120
            }} className="flex flex-col items-center text-center" data-id="element-83">
                  <div className="relative mb-5" data-id="element-84">
                    <div className={`w-16 h-16 ${step.bg} ${step.textColor} rounded-full flex items-center justify-center shadow-soft border-4 border-peach`} data-id="element-85">
                      <step.Icon size={26} strokeWidth={2.5} data-id="element-86" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-ink text-white rounded-full flex items-center justify-center font-black text-xs shadow-soft-sm" data-id="element-87">
                      {step.num}
                    </div>
                  </div>
                  <h3 className="text-xl font-extrabold text-ink mb-2" data-id="element-88">
                    {step.title}
                  </h3>
                  <p className="text-ink/70 font-medium max-w-[220px]" data-id="element-89">
                    {step.desc}
                  </p>
                </motion.div>)}
            </div>
          </div>

          {/* Mobile: vertical timeline */}
          <div className="md:hidden relative" data-id="element-90">
            <div className="absolute left-8 top-8 bottom-8 w-1 bg-white/80 rounded-full" data-id="element-91" />
            <div className="absolute left-8 top-8 bottom-8 w-1 rounded-full bg-gradient-to-b from-coral via-sunshine to-leaf opacity-60" data-id="element-92" />

            <div className="flex flex-col gap-8" data-id="element-93">
              {[{
              num: 1,
              Icon: ClipboardList,
              bg: 'bg-coral',
              textColor: 'text-white',
              title: 'Score your tasks',
              desc: 'Rate your weekly marketing tasks by time spent, impact, and repeatability.'
            }, {
              num: 2,
              Icon: BarChart3,
              bg: 'bg-sunshine',
              textColor: 'text-ink',
              title: 'See the leaderboard',
              desc: 'Our algorithm calculates the perfect priority score for every single task.'
            }, {
              num: 3,
              Icon: Target,
              bg: 'bg-leaf',
              textColor: 'text-white',
              title: 'Get your Top 3',
              desc: 'Walk away with a clear, actionable plan on what to hand off to AI first.'
            }].map((step, i) => <motion.div key={step.num} initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5 + i * 0.15,
              type: 'spring',
              stiffness: 120
            }} className="relative flex items-start gap-5 text-left" data-id="element-94">
                  <div className="relative flex-shrink-0" data-id="element-95">
                    <div className={`w-16 h-16 ${step.bg} ${step.textColor} rounded-full flex items-center justify-center shadow-soft border-4 border-peach`} data-id="element-96">
                      <step.Icon size={26} strokeWidth={2.5} data-id="element-97" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-ink text-white rounded-full flex items-center justify-center font-black text-xs shadow-soft-sm" data-id="element-98">
                      {step.num}
                    </div>
                  </div>
                  <div className="pt-2" data-id="element-99">
                    <h3 className="text-lg font-extrabold text-ink mb-1" data-id="element-100">
                      {step.title}
                    </h3>
                    <p className="text-ink/70 font-medium text-sm" data-id="element-101">
                      {step.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}